/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext } from "react";

// @material-ui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
//import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import AppBar from "@mui/material/AppBar";
import LinkedIn from "@mui/icons-material/LinkedIn";
import Twitter from "@mui/icons-material/Twitter";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
// Settings page components
import CrudService from "services/cruds-service";
import { line } from "stylis";
import { AuthContext } from "context";

// examples
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import FeaturesCard from "examples/Cards/FeaturesCard";
import {
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Modal,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import ReactPlayer from "react-player";
import CourseGoalModal from "./Dialog";

import { analytics } from "layouts/dashboards/analytics/analytics";
import { events } from "layouts/dashboards/analytics/events";

function CourseHome({
  courseId,
  switchStep,
  courseCompletionStatus,
  coursePurchaseStatus,
  moduleCount,
  courseInformation,
  paymentLink = "",
}) {
  const [learningLevelModal, setLearningLevelModal] = useState(false);
  const [paymentRedirectModalOpen, setPaymentRedirectModalOpen] = useState(false);
  const [user, setUser] = useState("");
  const [userRole, setUserRole] = useState("");
  const [shareDialog, setShareDialog] = useState(false);
  const { setIsAuthenticated, getCurrentUser, getRole } = useContext(AuthContext);
  const navigate = useNavigate();


  useEffect(() => {
    async function checkToken() {
      try {
        let user_id = await getCurrentUser();
        let user_role = await getRole();
        setUserRole(user_role);
        if (!user_id) {
          setIsAuthenticated(false);
          localStorage.removeItem("token");
        }
        return user_id;
      } catch (error) {
        console.log(error);
        alert("Error fetching the user. Please refresh or try again later.");
        navigate("/course-catalog");
      }
    }
    checkToken().then((userId) => setUser(userId));
  }, []);


  useEffect(() => {
    const start_time = Date.now();
    async function getUserId() {
      return await getCurrentUser();
    }
    getUserId().then((user_id) => {
      analytics.track(events.VIEW_PAGE, { page: "course-home", userId: user_id, courseId: courseId });
    });
    return () => {
      getUserId().then((user_id) => {
        analytics.track(events.PAGE_LEFT, { page: "course-home", userId: user_id, courseId: courseId, start_time: start_time, end_time: Date.now() });
      });
    }
  }, [courseId]);

  const handlePurchaseCourse = async (goal) => {
    try {
      const response = await CrudService.purchaseCourse({
        user_id: user,
        course_id: courseId,
        goal: goal,
      });


      if (response) {
        analytics.track(events.COURSE_ENROLLED, { userId: user, courseId: courseId, goal: goal });
        alert(
          "Course purchased successfully. The course is now unlocked for you in My Courses."
        );
        //  refresh the page
        window.location.reload();
      } else {
        alert("Error purchasing course. Please try again later.");
        navigate("/course-catalog");
      }
    } catch (error) {
      console.log(error);
      alert("Error purchasing the course. Please try again later.");
      navigate("/course-catalog");
    }
  };

  const handleRegisterUser = async () => {
    if (!user || user == "") {
      alert("Please login to purchase course.");
      return;
    }
    if (paymentLink) {
      setPaymentRedirectModalOpen(true);
      return;
    }
    setLearningLevelModal(true);
  };

  const handleRegisterFromPrompt = () => {
    if (!paymentLink) return;
    window.open(paymentLink, "_blank");
    setPaymentRedirectModalOpen(false);
  };

  const handleGoalSelection = async (goal) => {
    await handlePurchaseCourse(goal);
  };


  const [openCodelabModal, setOpenCodelabModal] = useState(false);

  const handleOpen = () => {
    setOpenCodelabModal(true);
  };

  const handleClose = () => {
    setOpenCodelabModal(false);
  };

  const handleShareCourse = (socialMediaPlatform) => {
    // handleShareDialogClose();
    let currentUrl = window.location.href;
    currentUrl = currentUrl.replace('course-content', 'course-information');

    if (socialMediaPlatform === "twitter") {
      const tweetText = `Level up your skills with the course: %0A${courseInformation.app_name}! %0A%0ACheck it out now:`;
      const twitterUrl = `https://twitter.com/intent/tweet?text=${tweetText}&url=${encodeURIComponent(currentUrl)}`;

      window.open(twitterUrl, "_blank");
    }
    else if (socialMediaPlatform === "linkedin") {
      const linkedInText = `Level up your skills with the course: ${courseInformation.app_name}!%0A %0AHere is a short description about the course: %0A${courseInformation.short_description}.%0A%0ACheck it out now: %0A${currentUrl}`;
      // Replace 'course-content' with 'course-information'
      const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?text=${linkedInText}&url=${encodeURIComponent(currentUrl)}`;

      window.open(linkedInUrl, "_blank");
    }
  };

  const handleCopyUrl = () => {
    // handleShareDialogClose();
    let currentUrl = window.location.href;
    currentUrl = currentUrl.replace('course-content', 'course-information');
    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        alert("URL copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy URL: ", err);
      });
  };

  const handleShareDialogClose = () => {
    setShareDialog(false);
  }

  return (
    <>
      <CourseGoalModal
        open={learningLevelModal}
        onClose={() => setLearningLevelModal(false)}
        onSelectGoal={handleGoalSelection}
        modalTitle="What do you want to accomplish with this course?"
        modalDescription="Based on your selected goal, we’ll personalize your learning experience.
                    Whether you want a brief overview or deep mastery, choose the path that aligns
                    with your learning intention."
        buttonText="LET’S BEGIN"
      />
      <Dialog
        open={paymentRedirectModalOpen}
        onClose={() => setPaymentRedirectModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogTitle sx={{ background: "#2563eb", py: 2.25 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: "white" }}>verified_user</Icon>
            <MDTypography variant="h6" color="white">
              Registration Required
            </MDTypography>
          </MDBox>
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          <MDBox
            sx={{
              backgroundColor: "#f8fbff",
              border: "1px solid #dbeafe",
              borderRadius: "10px",
              p: 2,
            }}
          >
            <MDTypography variant="body2">
              You need to register for this course to continue.
            </MDTypography>
            <MDBox display="flex" alignItems="center" mt={1}>
              <Icon fontSize="small" color="info" sx={{ mr: 0.75 }}>
                open_in_new
              </Icon>
              <MDTypography variant="body2">
                Clicking Register will take you to an external site.
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setPaymentRedirectModalOpen(false)}>
            Cancel
          </MDButton>
          <MDButton color="info" onClick={handleRegisterFromPrompt}>
            <Icon sx={{ fontSize: "1rem", mr: 0.5 }}>open_in_new</Icon>
            Register
          </MDButton>
        </DialogActions>
      </Dialog>

      <Card id="basic-info" sx={{ overflow: "visible" }}>
        {
          <MDBox>
            <Grid container spacing={4} p={4} pb={0}>
              {/* Left Content: Title, Description, Podcast, and Course Introduction */}
              {/* <Grid item xs={12} lg={8} pr={4} sx={{ borderRight: { lg: "1px solid #e0e0e0" } }}> */}
              <Grid
                item
                xs={12}
                lg={8}
                pr={4}
                sx={{
                  position: "relative", // Required for pseudo-element
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "1px", // Base width
                    height: "100%",
                    background: "linear-gradient(to bottom, transparent, #e0e0e0 50%, transparent)",
                    opacity: 5, // Ensures opacity is applied
                  },
                }}
              >

                <MDTypography variant="h2" mr={2} mb={2}>
                  {courseInformation.app_name}
                </MDTypography>

                {/* Short Description */}
                <div data-color-mode="light">
                  <MDEditor.Markdown
                    source={courseInformation.short_description}
                    style={{ color: "#344767" }}
                    components={{
                      p: ({ node, ...props }) => <p style={{ color: "#7b809a" }} {...props} />,
                      a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                    }}
                  />
                </div>

                {/* Course Introduction */}
                <div data-color-mode="light" style={{ marginTop: "16px" }}>
                  <MDEditor.Markdown
                    source={courseInformation.home_page_introduction}
                    style={{ color: "#344767" }}
                    components={{
                      p: ({ node, ...props }) => <p style={{ color: "#7b809a" }} {...props} />,
                      a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                    }}
                  />
                </div>
              </Grid>

              {/* Right Content: Image, Course Features, and Course Preview */}
              <Grid item xs={12} lg={4} pl={4}>
                <MDBox
                  component="img"
                  src={courseInformation.app_image_location}
                  alt={courseInformation.app_name}
                  borderRadius="lg"
                  shadow="md"
                  width="100%"
                  mb={3}
                />



                {courseInformation?.podcastLink?.trim() && (
                  <>
                    {/* Course Features */}
                    <MDTypography variant="h4" my={2}>
                      Course Audio Guide
                    </MDTypography>

                    <audio controls style={{ display: "block", width: "100%" }}>
                      <source src={courseInformation.podcastLink} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  </>

                )}

                {/* Course Features */}
                {/* <MDTypography variant="h4" my={2}>
                  Course Features
                </MDTypography>

                <MDBox pt={2}>
                  <FeaturesCard
                    color="info"
                    icon="view_module"
                    title="Course Modules"
                    count={moduleCount}
                  />
                </MDBox> */}

                {courseInformation.hasQuiz && (
                  <>
                    <MDBox p={2}>
                      <FeaturesCard color="info" icon="quiz" title="Contains Assessment" count={"Quiz"} />
                    </MDBox>
                    <MDBox p={2}>
                      <FeaturesCard color="info" icon="verified" title="Proof of Completion" count={"Certificate"} />
                    </MDBox>
                  </>
                )}

                {/* Course Preview */}
                {courseInformation?.previewVideoLink && courseInformation?.previewSlideDeckLink && (
                  <>
                    <MDTypography variant="h4" my={2}>
                      Course Preview
                    </MDTypography>
                    <Card
                      style={{
                        padding: "20px",
                        borderTop: "2px solid #f5f5f5",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      {/* Video Player - Fully Contained */}
                      <div style={{ width: "100%", position: "relative", aspectRatio: "16 / 9" }}>
                        <ReactPlayer
                          url={courseInformation.previewVideoLink}
                          controls
                          width="100%"
                          height="100%"
                          style={{ position: "absolute", top: 0, left: 0 }}
                          config={{ file: { attributes: { controlsList: "nodownload" } } }}
                        />
                      </div>

                      <Divider style={{ margin: "30px 0", opacity: 0.5, width: "100%" }} />

                      {/* Slide Deck - Fully Contained */}
                      <div style={{ width: "100%", position: "relative" }}>
                        <iframe
                          src={courseInformation.previewSlideDeckLink}
                          title="PDF Document"
                          style={{
                            width: "100%",
                            height: "100%",
                            minHeight: "350px",
                            border: "none",
                          }}
                        />
                      </div>
                    </Card>
                  </>
                )}

              </Grid>
            </Grid>
            <Divider style={{ opacity: 5 }} />
            <Grid container px={4} mb={2} spacing={2}>
              <Grid item xs={12}>
                {courseInformation.disclaimer && (
                  <MDBox>
                    <MDTypography
                      p={1}
                      style={{
                        marginTop: "1rem",
                        background: "#fefefe",
                        fontStyle: "italic",
                      }}
                      color="secondary"
                      alignItems="center"
                      variant="body2"
                    >
                      {"*" + courseInformation.disclaimer}
                    </MDTypography>
                  </MDBox>
                )}
              </Grid>
            </Grid>


            <Grid container px={4} mb={4}>
              {coursePurchaseStatus ? (<Grid item xs={6} lg={4} p={1}>
                <MDButton
                  color="warning"
                  fullWidth
                  onClick={() => switchStep(1)}
                >
                  Go to Modules &nbsp;
                  <Icon>navigate_next</Icon>
                </MDButton>
              </Grid>
              ) : (
                <>
                  <Grid item xs={6} lg={4} p={1}>
                    <MDButton
                      color="info"
                      variant="outlined"
                      fullWidth
                      onClick={handleRegisterUser}
                    >
                      Register
                    </MDButton>
                  </Grid>
                </>
              )}

              <>
                {/* LinkedIn and Twitter Icons */}

                <Grid item>
                  <IconButton
                    color="info"
                    onClick={() => handleShareCourse("linkedin")}
                    aria-label="Share on LinkedIn"
                  >
                    <LinkedIn fontSize="large" />
                  </IconButton>
                </Grid>
                <Grid item>
                  <IconButton
                    color="info"
                    onClick={() => handleShareCourse("twitter")}
                    aria-label="Share on Twitter"
                  >
                    <Twitter fontSize="large" />
                  </IconButton>
                </Grid>
                <Grid item >
                  <IconButton
                    color="info"
                    onClick={handleCopyUrl}
                    aria-label="Copy URL"
                  >
                    <LinkIcon fontSize="large" color="info" /> {/* Small icon */}
                  </IconButton>
                </Grid>
              </>
            </Grid>
          </MDBox>
        }
      </Card >

      <Dialog open={shareDialog} onClose={handleShareDialogClose} fullWidth>
        <DialogTitle style={{ textAlign: "center" }}>Share Course</DialogTitle>
        <DialogContent>
          {/* LinkedIn and Twitter Icons */}
          <Grid
            container
            justifyContent="center"
            spacing={2}
          >
            <Grid item>
              <IconButton
                color="info"
                onClick={() => handleShareCourse("linkedin")}
                aria-label="Share on LinkedIn"
              >
                <LinkedIn fontSize="large" />
              </IconButton>
            </Grid>
            <Grid item>
              <IconButton
                color="info"
                onClick={() => handleShareCourse("twitter")}
                aria-label="Share on Twitter"
              >
                <Twitter fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>

          <Card style={{ marginTop: "20px", padding: "10px", backgroundColor: "#f5f5f5" }}>
            <Grid container alignItems="center">
              <Grid item xs={10}>
                <MDTypography
                  variant="caption1"
                  component="div"
                  style={{
                    marginRight: "10px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    width: "100%",
                  }}
                >
                  {window.location.href.replace('course-content', 'course-information')}
                </MDTypography>
              </Grid>
              <Grid item xs={2} style={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton
                  color="primary"
                  onClick={handleCopyUrl}
                  aria-label="Copy URL"
                  style={{ padding: "4px", fontSize: "18px" }} // Reduced icon size and padding
                >
                  <ContentCopyIcon fontSize="small" color="secondary" /> {/* Small icon */}
                </IconButton>
              </Grid>
            </Grid>
          </Card>
        </DialogContent>
      </Dialog>
    </>
  );

  // return (
  //   <>
  //     <Card id="basic-info" sx={{ overflow: "visible" }}>
  //       {
  //         <MDBox>
  //           <Grid container p={4} pb={0}>
  //             <Grid item xs={12} lg={8}>
  //               <MDTypography variant="h2" mr={2}>
  //                 {courseInformation.app_name}
  //               </MDTypography>
  //               {courseInformation?.podcastLink?.trim() && (
  //                 <MDBox mt={3}>
  //                   <audio controls style={{ display: "block", width: "100%", paddingRight: "20px" }}>
  //                     <source src={courseInformation.podcastLink} type="audio/mpeg" />
  //                     Your browser does not support the audio element.
  //                   </audio>
  //                 </MDBox>
  //               )}

  //               {/* {courseInformation.codelabs.length > 0 && (
  //                 <>
  //                   <MDButton
  //                     variant="gradient"
  //                     color="info"
  //                     onClick={handleOpen}
  //                   >
  //                     Open Codelabs
  //                   </MDButton>
  //                   <Modal
  //                     open={openCodelabModal}
  //                     onClose={handleClose}
  //                     aria-labelledby="simple-modal-title"
  //                     aria-describedby="simple-modal-description"
  //                     // put the card in the center of the screen
  //                     style={{
  //                       display: "flex",
  //                       alignItems: "center",
  //                       justifyContent: "center",
  //                     }}
  //                   >
  //                     <Card
  //                       style={{
  //                         width: "50%",
  //                         padding: "20px",
  //                         justifyItems: "center",
  //                         display: "flex",
  //                       }}
  //                     >
  //                       <MDTypography
  //                         variant="h4"
  //                         // center it
  //                         style={{
  //                           textAlign: "center",
  //                         }}
  //                         mb={2}
  //                       >
  //                         Codelabs
  //                       </MDTypography>
  //                       <MDTypography variant="body2" ml={1}>
  //                         Click below links to be redirected to a Google Colab.
  //                       </MDTypography>
  //                       <List>
  //                         {courseInformation.codelabs.map((codelab, index) => (
  //                           <ListItem
  //                             key={index}
  //                             button
  //                             onClick={() => {
  //                               window.open(codelab.link, "_blank");
  //                             }}
  //                             style={{
  //                               padding: "10px",
  //                             }}
  //                           >
  //                             <ListItemText
  //                               primary={codelab.title}
  //                               secondary={codelab.subtitle}
  //                             />
  //                             <ListItemSecondaryAction>
  //                               <Tooltip title="Open Codelab">
  //                                 <Icon>open_in_new</Icon>
  //                               </Tooltip>
  //                             </ListItemSecondaryAction>
  //                           </ListItem>
  //                         ))}
  //                       </List>
  //                     </Card>
  //                   </Modal>
  //                 </>
  //               )} */}
  //               {/* <MDTypography my={4} mr={2} variant="body1" color="text">
  //                 {courseInformation.short_description}
  //               </MDTypography> */}
  //               <div data-color-mode='light'>
  //                 <MDEditor.Markdown source={courseInformation.short_description} style={{ color: "#344767" }} components={{
  //                   p: ({ node, ...props }) => <p style={{ color: "#7b809a" }} {...props} />,
  //                   a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
  //                 }} />
  //               </div>
  //               <div data-color-mode='light'>
  //                 <MDEditor.Markdown source={courseIntro} style={{ color: "#344767" }} components={{
  //                   p: ({ node, ...props }) => <p style={{ color: "#7b809a" }} {...props} />,
  //                   a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
  //                 }} />
  //               </div>
  //             </Grid>
  //             <Grid item xs={12} lg={4}>
  //               <MDBox
  //                 component="img"
  //                 src={courseInformation.app_image_location}
  //                 alt={courseInformation.app_name}
  //                 borderRadius="lg"
  //                 shadow="md"
  //                 width="100%"
  //                 position="relative"
  //                 zIndex={1}
  //               />
  //               <>
  //                 <MDTypography variant="h4" my={2}>
  //                   Course Features
  //                 </MDTypography>
  //                 <MDBox p={2}>
  //                   <FeaturesCard
  //                     color="info"
  //                     icon="view_module"
  //                     title="Course Modules"
  //                     count={courseInformation.course_names_videos.length}
  //                   />
  //                 </MDBox>
  //                 {
  //                   courseInformation.hasQuiz && (
  //                     <>
  //                       <MDBox p={2}>
  //                         <FeaturesCard
  //                           color="info"
  //                           icon="quiz"
  //                           title="Contains Assessment"
  //                           count={"Quiz"}
  //                         />
  //                       </MDBox>
  //                       <MDBox p={2}>
  //                         <FeaturesCard
  //                           color="info"
  //                           icon="verified"
  //                           title="Proof of Completion"
  //                           count={"Certificate"}
  //                         />
  //                       </MDBox>
  //                     </>
  //                   )}
  //                 {courseInformation?.previewVideoLink && courseInformation?.previewSlideDeckLink && (
  //                   <>
  //                     <MDTypography variant="h4" my={2}>
  //                       Course Preview
  //                     </MDTypography>
  //                     <Grid item xs={12} pt={0}>
  //                       <Card
  //                         style={{
  //                           padding: "20px",
  //                           borderTop: "2px solid #f5f5f5", // Adds a light visible top border
  //                         }}
  //                       >
  //                         <div style={{ position: "relative", paddingTop: "56.25%" }}>
  //                           <ReactPlayer
  //                             url={courseInformation.previewVideoLink}
  //                             controls
  //                             config={{
  //                               file: { attributes: { controlsList: "nodownload" } },
  //                             }}
  //                             style={{
  //                               position: "absolute",
  //                               top: 0,
  //                               left: 0,
  //                               width: "100%",
  //                               height: "100%",
  //                             }}
  //                             width="100%"
  //                             height="100%"
  //                           />
  //                         </div>
  //                         <Divider style={{ margin: "30px 0", opacity: 5 }} />
  //                         <div style={{ width: "100%" }}>
  //                           <iframe
  //                             src={courseInformation.previewSlideDeckLink}
  //                             title="PDF Document"
  //                             style={{
  //                               width: "100%",
  //                               minHeight: window.innerWidth < 400 ? "200px" : "350px",
  //                               border: "none",
  //                             }}
  //                           />
  //                         </div>
  //                       </Card>
  //                     </Grid>
  //                   </>
  //                 )}


  //               </>


  //             </Grid>
  //           </Grid>
  //           <Divider />
  //           <Grid container px={4} mb={2} spacing={2}>
  //             {/* <Grid item xs={12} lg={8} p={2}>
  //               <div data-color-mode='light'>
  //                 <MDEditor.Markdown source={courseIntro} style={{ color: "#344767" }} components={{
  //                   p: ({ node, ...props }) => <p style={{ color: "#7b809a" }} {...props} />,
  //                   a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
  //                 }} />
  //               </div>
  //             </Grid> */}
  //             {/* <Grid item xs={12} lg={4}>
  //               <MDTypography variant="h4" my={2}>
  //                 Course Features
  //               </MDTypography>
  //               <MDBox p={2}>
  //                 <FeaturesCard
  //                   color="info"
  //                   icon="view_module"
  //                   title="Course Modules"
  //                   count={courseInformation.course_names_videos.length}
  //                 />
  //               </MDBox>
  //               {
  //                 courseInformation.hasQuiz && (
  //                   <>
  //                     <MDBox p={2}>
  //                       <FeaturesCard
  //                         color="info"
  //                         icon="quiz"
  //                         title="Contains Assessment"
  //                         count={"Quiz"}
  //                       />
  //                     </MDBox>
  //                     <MDBox p={2}>
  //                       <FeaturesCard
  //                         color="info"
  //                         icon="verified"
  //                         title="Proof of Completion"
  //                         count={"Certificate"}
  //                       />
  //                     </MDBox>
  //                   </>
  //                 )}
  //               {courseInformation?.previewVideoLink && courseInformation?.previewSlideDeckLink && (
  //                 <>
  //                   <MDTypography variant="h4" my={2}>
  //                     Course Preview
  //                   </MDTypography>
  //                   <Grid item xs={12} pt={0}>
  //                     <Card
  //                       style={{
  //                         padding: "20px",
  //                         borderTop: "2px solid #f5f5f5", // Adds a light visible top border
  //                       }}
  //                     >
  //                       <div style={{ position: "relative", paddingTop: "56.25%" }}>
  //                         <ReactPlayer
  //                           url={courseInformation.previewVideoLink}
  //                           controls
  //                           config={{
  //                             file: { attributes: { controlsList: "nodownload" } },
  //                           }}
  //                           style={{
  //                             position: "absolute",
  //                             top: 0,
  //                             left: 0,
  //                             width: "100%",
  //                             height: "100%",
  //                           }}
  //                           width="100%"
  //                           height="100%"
  //                         />
  //                       </div>
  //                       <Divider style={{ margin: "30px 0", opacity: 5 }} />
  //                       <div style={{ width: "100%" }}>
  //                         <iframe
  //                           src={courseInformation.previewSlideDeckLink}
  //                           title="PDF Document"
  //                           style={{
  //                             width: "100%",
  //                             minHeight: window.innerWidth < 400 ? "200px" : "350px",
  //                             border: "none",
  //                           }}
  //                         />
  //                       </div>
  //                     </Card>
  //                   </Grid>
  //                 </>
  //               )}


  //             </Grid> */}
  //             <Grid item xs={12}>
  //               {courseInformation.disclaimer && (
  //                 <MDBox>
  //                   <MDTypography
  //                     p={1}
  //                     style={{
  //                       marginTop: "1rem",
  //                       background: "#fefefe",
  //                       fontStyle: "italic",
  //                     }}
  //                     color="secondary"
  //                     alignItems="center"
  //                     variant="body2"
  //                   >
  //                     {"*" + courseInformation.disclaimer}
  //                   </MDTypography>
  //                 </MDBox>
  //               )}
  //             </Grid>
  //           </Grid>

  //           {/* <Grid container px={4} mb={4}>
  //           <Grid item xs={12} lg={12}>
  //             <MDTypography variant="h4" my={1}>
  //               Course Preview
  //             </MDTypography>
  //             <MDBox
  //               p={1}
  //               style={{
  //                 border: "1px solid #e0e0e0",
  //                 // make the height either the height of the course modules or 40vh
  //                 height: courseModules.length > 4 ? "40vh" : "auto",
  //                 overflow: "auto",
  //               }}
  //             >
  //               {courseModules.map((module, index) => (
  //                 <MDBox
  //                   key={index}
  //                   p={2}
  //                   display="flex"
  //                   style={{
  //                     borderLeft: "10px solid gray",
  //                     border: "1px solid #e0e0e0",
  //                     alignItems: "center",
  //                   }}
  //                 >
  //                   <MDBox
  //                     display="flex"
  //                     style={{
  //                       width: "100%",
  //                       flexDirection: "column",
  //                     }}
  //                   >
  //                     <MDTypography variant="body1">
  //                       {index + 1}. {module}
  //                     </MDTypography>
  //                   </MDBox>
  //                   <MDBox
  //                     display="flex"
  //                     justifyContent="flex-end"
  //                     mt={2}
  //                     p={1}
  //                     style={{
  //                       alignItems: "center",
  //                     }}
  //                   >
  //                     <Icon color="info" fontSize="small">
  //                       slow_motion_video_outlined
  //                     </Icon>
  //                     <Icon color="dark" fontSize="small">
  //                       slideshow_outlined
  //                     </Icon>
  //                   </MDBox>
  //                 </MDBox>
  //               ))}
  //             </MDBox>
  //           </Grid>
  //         </Grid> */}
  //           <Grid container px={4} mb={4}>
  //             {coursePurchaseStatus ? (
  //               courseCompletionStatus ? (
  //                 <Grid item xs={6} lg={4} p={1}>
  //                   <MDButton
  //                     color="success"
  //                     fullWidth
  //                     onClick={() => switchStep(1)}
  //                   >
  //                     Review &nbsp;
  //                     <Icon>navigate_next</Icon>
  //                   </MDButton>
  //                 </Grid>
  //               ) : (
  //                 <Grid item xs={6} lg={4} p={1}>
  //                   <MDButton
  //                     color="warning"
  //                     fullWidth
  //                     onClick={() => switchStep(1)}
  //                   >
  //                     Go to Modules &nbsp;
  //                     <Icon>navigate_next</Icon>
  //                   </MDButton>
  //                 </Grid>
  //               )
  //             ) : (
  //               <>
  //                 <Grid item xs={6} lg={4} p={1}>
  //                   <MDButton
  //                     color="info"
  //                     variant="outlined"
  //                     fullWidth
  //                     onClick={handlePurchaseCourse}
  //                   >
  //                     Register
  //                   </MDButton>
  //                 </Grid>
  //               </>
  //             )}

  //             <>
  //               {/* LinkedIn and Twitter Icons */}

  //               <Grid item>
  //                 <IconButton
  //                   color="info"
  //                   onClick={() => handleShareCourse("linkedin")}
  //                   aria-label="Share on LinkedIn"
  //                 >
  //                   <LinkedIn fontSize="large" />
  //                 </IconButton>
  //               </Grid>
  //               <Grid item>
  //                 <IconButton
  //                   color="info"
  //                   onClick={() => handleShareCourse("twitter")}
  //                   aria-label="Share on Twitter"
  //                 >
  //                   <Twitter fontSize="large" />
  //                 </IconButton>
  //               </Grid>
  //               <Grid item >
  //                 <IconButton
  //                   color="info"
  //                   onClick={handleCopyUrl}
  //                   aria-label="Copy URL"
  //                 >
  //                   <LinkIcon fontSize="large" color="info" /> {/* Small icon */}
  //                 </IconButton>
  //               </Grid>
  //             </>


  //             {/* {userRole === "admin" && (
  //               <>
  //                 <Grid item xs={6} lg={4} p={1}>
  //                   <MDButton
  //                     color="info"
  //                     variant="outlined"
  //                     fullWidth
  //                     onClick={handleShareDialogOpen}
  //                     startIcon={<Icon>share_icon</Icon>}
  //                   >
  //                     Share
  //                   </MDButton>
  //                 </Grid>
  //               </>
  //             )} */}

  //           </Grid>
  //         </MDBox>
  //       }
  //     </Card >

  //     <Dialog open={shareDialog} onClose={handleShareDialogClose} fullWidth>
  //       <DialogTitle style={{ textAlign: "center" }}>Share Course</DialogTitle>
  //       <DialogContent>
  //         {/* LinkedIn and Twitter Icons */}
  //         <Grid
  //           container
  //           justifyContent="center"
  //           spacing={2}
  //         >
  //           <Grid item>
  //             <IconButton
  //               color="info"
  //               onClick={() => handleShareCourse("linkedin")}
  //               aria-label="Share on LinkedIn"
  //             >
  //               <LinkedIn fontSize="large" />
  //             </IconButton>
  //           </Grid>
  //           <Grid item>
  //             <IconButton
  //               color="info"
  //               onClick={() => handleShareCourse("twitter")}
  //               aria-label="Share on Twitter"
  //             >
  //               <Twitter fontSize="large" />
  //             </IconButton>
  //           </Grid>
  //         </Grid>

  //         <Card style={{ marginTop: "20px", padding: "10px", backgroundColor: "#f5f5f5" }}>
  //           <Grid container alignItems="center">
  //             <Grid item xs={10}>
  //               <MDTypography
  //                 variant="caption1"
  //                 component="div"
  //                 style={{
  //                   marginRight: "10px",
  //                   whiteSpace: "nowrap",
  //                   overflow: "hidden",
  //                   textOverflow: "ellipsis",
  //                   display: "block",
  //                   width: "100%",
  //                 }}
  //               >
  //                 {window.location.href.replace('course-content', 'course-information')}
  //               </MDTypography>
  //             </Grid>
  //             <Grid item xs={2} style={{ display: "flex", justifyContent: "flex-end" }}>
  //               <IconButton
  //                 color="primary"
  //                 onClick={handleCopyUrl}
  //                 aria-label="Copy URL"
  //                 style={{ padding: "4px", fontSize: "18px" }} // Reduced icon size and padding
  //               >
  //                 <ContentCopyIcon fontSize="small" color="secondary" /> {/* Small icon */}
  //               </IconButton>
  //             </Grid>
  //           </Grid>
  //         </Card>
  //       </DialogContent>
  //     </Dialog>
  //   </>
  // );
}

export default CourseHome;
